import { ComponentType, useMemo } from 'react';
import { useSignUpStore } from '@shared/store/useSignUpStore';
import { createSignUpFormSubmitHandler, getSignUpFormCopy } from '@modules/sign-up/business_logic';
import { useSignUpSubmit } from '../hooks/useSignUpSubmit';
import { useGuestSignIn } from '@modules/auth/hooks/useGuestSignIn';
import type { SignUpProps } from '../views/sign_up_view';

interface SignUpContainerProps {
  View: ComponentType<SignUpProps>;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onSuccessNavigate: () => void;
  onNavigateToLogin: () => void;
  /** When true, wires `onFormSubmit` for HTML form preventDefault (web). */
  useFormSubmitHandler?: boolean;
}

export function SignUpContainer({
  View,
  onSuccess,
  onError,
  onSuccessNavigate,
  onNavigateToLogin,
  useFormSubmitHandler = false,
}: SignUpContainerProps) {
  const isSubmitting = useSignUpStore((s) => s.isSubmitting);
  const form = useSignUpStore((s) => s.form);
  const setForm = useSignUpStore((s) => s.setForm);
  const setIsSubmitting = useSignUpStore((s) => s.setIsSubmitting);
  const reset = useSignUpStore((s) => s.reset);
  const handleSubmit = useSignUpSubmit(onSuccess, onError, onSuccessNavigate);
  const handleGuestSignIn = useGuestSignIn(
    onSuccess,
    onError,
    onSuccessNavigate,
    setIsSubmitting,
    reset
  );
  const handleEmailChange = (email: string) => setForm({ email });
  const handlePasswordChange = (password: string) => setForm({ password });

  const formCopy = useMemo(() => getSignUpFormCopy(isSubmitting), [isSubmitting]);

  const onFormSubmit = useMemo(
    () => (useFormSubmitHandler ? createSignUpFormSubmitHandler(handleSubmit) : undefined),
    [useFormSubmitHandler, handleSubmit]
  );

  return (
    <View
      {...formCopy}
      isSubmitting={isSubmitting}
      email={form.email}
      password={form.password}
      onEmailChange={handleEmailChange}
      onPasswordChange={handlePasswordChange}
      onNavigateToLogin={onNavigateToLogin}
      onSubmit={handleSubmit}
      onGuestSignIn={handleGuestSignIn}
      onFormSubmit={onFormSubmit}
    />
  );
}

export type { SignUpProps };
